import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router';
import { theme } from '../../config/theme';
import { activeArea, visibleAreas } from '../../config/areas';
import { useAuth } from '../../hooks/useAuth';
import { useCountryLanguageDefault } from '../../hooks/useCountryLanguageDefault';
import { RequireRole } from '../auth/RequireRole';
import { AreaNav } from './AreaNav';
import { ManagementLink } from './ManagementLink';
import { UserMenu } from './UserMenu';

interface AppShellProps {
  requiredRoles: readonly string[];
}

/** Area pages read roles from here rather than fetching /.auth/me again. */
export interface AreaContext {
  roles: string[];
}

/**
 * The end-user experience: Documents only. Every signed-in user, admin included, lands here -
 * management destinations (Template Studio, Administration) live behind ManagementShell instead.
 */
export function AppShell({ requiredRoles }: AppShellProps) {
  const { t } = useTranslation();
  const { user, roles, loading } = useAuth();
  const { pathname } = useLocation();
  const current = activeArea(pathname);
  const areas = visibleAreas(roles);
  const managementAreas = areas.filter((area) => area.key === 'studio' || area.key === 'settings');
  // Reachable from every page, not just the Documents list, so starting a document never depends on which tab you're on.
  const canCreate = areas.some((area) => area.key === 'documents');

  useCountryLanguageDefault();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-surface">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-5 py-3 sm:gap-4 sm:px-6 lg:px-8">
          <Link to="/app" className="shrink-0 font-heading font-semibold whitespace-nowrap text-foreground">
            {theme.brand.shortName}
          </Link>

          {current ? <AreaNav area={current} roles={roles} /> : null}

          <div className="ml-auto flex items-center gap-2">
            {canCreate ? (
              <Link
                to="/app/new"
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <PlusIcon className="size-4" aria-hidden="true" />
                {t('nav.create')}
              </Link>
            ) : null}
            <ManagementLink areas={managementAreas} current={current} roles={roles} />
            <UserMenu user={user} roles={roles} />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-6 sm:px-6 lg:px-8">
        <RequireRole anyOf={requiredRoles} roles={roles} loading={loading}>
          <Outlet context={{ roles } satisfies AreaContext} />
        </RequireRole>
      </main>
    </div>
  );
}


